import React, { useState, useEffect } from "react";
import { Plus, Pencil } from "lucide-react";
import { getRevenues, createRevenue, updateRevenue } from "../../../services/AdminPortal/financeAPI";

const RevenueMonitoring = ({ onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [sourceName, setSourceName] = useState("");
  const [amount, setAmount] = useState("");
  const [revenue, setRevenue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenues();
  }, []);

  const fetchRevenues = async () => {
    try {
      const data = await getRevenues();
      setRevenue(data);
    } catch (error) {
      console.error("Error fetching revenues:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!sourceName || !amount) return;
    
    try {
      const payload = {
        source: sourceName,
        amount: parseFloat(amount),
      };

      if (editingItem) {
        await updateRevenue(editingItem._id, payload);
      } else {
        await createRevenue(payload);
      }
      
      setShowModal(false);
      resetForm();
      await fetchRevenues();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error saving revenue:", error);
    }
  };

  const resetForm = () => {
    setSourceName("");
    setAmount("");
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setSourceName(item.source);
    setAmount(item.amount.toString());
    setShowModal(true);
  };

  return (
    <div className="bg-[#131313] h-full border border-[#333] p-4 rounded-lg w-full flex flex-col relative">
      <h2 className="text-white text-sm font-semibold mb-2">REVENUE MONITORING</h2>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center text-gray-400 py-4">Loading...</div>
        ) : (
          <table className="w-full text-[12px] text-gray-200 border-collapse">
            <thead>
              <tr className="text-gray-400 text-[11px]">
                <th className="text-left pb-2">SOURCE</th>
                <th className="text-right pb-2 pr-1">AMOUNT EARNED</th>
              </tr>
            </thead>
            <tbody>
              {revenue.map((item, idx) => (
                <tr key={item._id || idx} className="border-none">
                  <td className="py-1.5 border-none">{item.source}</td>
                  <td className="text-right py-1.5 pr-1 border-none flex justify-end items-center gap-1">
                    ₹{item.amount.toLocaleString("en-IN")}
                    <Pencil 
                      size={12} 
                      className="text-yellow-300 ml-3 cursor-pointer" 
                      onClick={() => handleEdit(item)}
                    />
                  </td>
                </tr>
              ))}
              {revenue.length === 0 && (
                <tr>
                  <td colSpan="2" className="text-center text-gray-400 py-4">
                    No revenue sources found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pt-3">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-2 bg-[#4b3f1c] text-yellow-300 text-sm px-4 py-2 rounded w-full"
        >
          <Plus size={16} />
          Add Revenue Source
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-md shadow-lg border border-[#333]">
            <h3 className="text-white text-base font-semibold mb-4">
              {editingItem ? "Edit Revenue Source" : "Add Revenue Source"}
            </h3>

            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">Source Name</label>
              <input
                type="text"
                placeholder="Enter source name"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
              />
            </div>

            <div className="mb-5">
              <label className="text-gray-400 text-sm block mb-1">Amount Earned</label>
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="bg-[#2b2b2b] text-white px-4 py-2 rounded text-sm border border-[#444]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!sourceName || !amount}
                className="bg-yellow-400 text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
              >
                {editingItem ? "Update" : "Confirm"} ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueMonitoring;